import type { components } from '@/generated/tracker-api'
import { newIdempotencyKey } from '@/shared/attachments/idempotencyKey'

import { CoreError } from './errors'
import { exhaustCursor } from './pagination'
import type { TrackerAttachmentInput } from './trackerClient'
import { API_ROOT, asRecord, asString, request } from './transport'

/** Report wire shapes come from the generated public contract. Runtime readers
 * still validate provider responses before exposing them to the workspace. */
export const TRACKER_REPORTS_ROOT = `${API_ROOT}/tracker/reports`
export type TrackerReportScope = 'mine' | 'all'
export type TrackerReportPerson = components['schemas']['TrackerReportAuthor']
export type TrackerReportPermissions = components['schemas']['TrackerReportPermissions']
export type TrackerReportAttachment = components['schemas']['TrackerReportAsset']
export type TrackerReportComment = components['schemas']['TrackerReportComment']
export type TrackerReportSummary = components['schemas']['TrackerReportSummary']
export type TrackerReportState = TrackerReportSummary['state']
export type TrackerReportPage = components['schemas']['TrackerReportList']
export type TrackerReportDetail = Omit<components['schemas']['TrackerReportDetail'], 'attachmentWarning'> & {
  attachmentWarning: string | null
}

/**
 * The 409 codes the reports routes answer with beside the shared ones. They
 * are matched BY CODE at the surface because the generated error union does
 * not know them yet and the shared status map reads every 409 as one
 * conflict; these three want three different sentences. A tracker outage is
 * a plain 503 (`service_unavailable`) and is read by KIND like any other.
 */
export const TRACKER_REPORT_CHANGED = 'tracker_report_changed'
export const TRACKER_REPORT_READ_ONLY = 'tracker_report_read_only'
export const TRACKER_OPERATION_UNCERTAIN = 'tracker_operation_uncertain'

/** A write attempt's key, in the format the tracker routes already take. */
export function trackerReportRequestKey(): string {
  return `report-${newIdempotencyKey()}`
}

export async function listTrackerReportsPage(
  scope: TrackerReportScope,
  before: number | undefined,
  signal?: AbortSignal,
): Promise<TrackerReportPage> {
  const body = await request('GET', TRACKER_REPORTS_ROOT, {
    query: { scope, ...(before === undefined ? {} : { before }) },
    ...(signal ? { signal } : {}),
  })
  const record = asRecord(body)
  const items = record?.['items']
  const nextBefore = record?.['nextBefore']
  if (!record || !Array.isArray(items) || !(nextBefore === null || isPositiveInteger(nextBefore))) {
    throw new CoreError('malformed', 'tracker report page is incomplete')
  }
  return {
    items: items.map((item) => reportSummary(item)),
    nextBefore: nextBefore === null ? null : Number(nextBefore),
  }
}

/**
 * THE WHOLE LIST, walked to its end. The registry table this feeds holds a
 * complete answer and pages it in the browser, so the server's keyset is a
 * transport detail here; the shared walker stops a server that never says
 * `null`. Twenty rows a page against a tracker of a few hundred reports is a
 * handful of requests, and a partial list would make every column filter
 * mean "among the rows that happened to load".
 */
export async function listTrackerReports(
  scope: TrackerReportScope,
  signal?: AbortSignal,
): Promise<TrackerReportSummary[]> {
  return exhaustCursor(`tracker reports (${scope})`, async (cursor) => {
    const page = await listTrackerReportsPage(scope, cursor === undefined ? undefined : Number(cursor), signal)
    return { items: page.items, nextCursor: page.nextBefore === null ? null : String(page.nextBefore) }
  })
}

export async function getTrackerReport(number: number, signal?: AbortSignal): Promise<TrackerReportDetail> {
  const body = await request('GET', `${TRACKER_REPORTS_ROOT}/${number}`, signal ? { signal } : {})
  return reportDetail(body, number)
}

export interface UpdateTrackerReportInput {
  number: number
  revision: string
  title: string
  description: string
  idempotencyKey: string
}

export async function updateTrackerReport(input: UpdateTrackerReportInput): Promise<TrackerReportDetail> {
  const body = await request('PATCH', `${TRACKER_REPORTS_ROOT}/${input.number}`, {
    body: { revision: input.revision, title: input.title, description: input.description },
    idempotencyKey: input.idempotencyKey,
  })
  return reportDetail(body, input.number)
}

export interface TrackerReportRevisionInput {
  number: number
  revision: string
  idempotencyKey: string
}

export async function closeTrackerReport(input: TrackerReportRevisionInput): Promise<TrackerReportDetail> {
  const body = await request('POST', `${TRACKER_REPORTS_ROOT}/${input.number}/close`, {
    body: { revision: input.revision },
    idempotencyKey: input.idempotencyKey,
  })
  return reportDetail(body, input.number)
}

export async function deleteTrackerReport(input: TrackerReportRevisionInput): Promise<number> {
  const body = await request('DELETE', `${TRACKER_REPORTS_ROOT}/${input.number}`, {
    body: { revision: input.revision },
    idempotencyKey: input.idempotencyKey,
  })
  const record = asRecord(body)
  if (!record || record['deleted'] !== true || !isPositiveInteger(record['number']) || record['number'] !== input.number) {
    throw new CoreError('malformed', 'tracker report deletion response is incomplete')
  }
  return Number(record['number'])
}

export interface CommentTrackerReportInput {
  number: number
  revision: string
  description: string
  attachments: readonly TrackerAttachmentInput[]
  idempotencyKey: string
}

export async function commentTrackerReport(input: CommentTrackerReportInput): Promise<TrackerReportDetail> {
  const form = new FormData()
  form.set('description', input.description)
  form.set('revision', input.revision)
  // `append`, not `set`: the two attachment fields are repeated and PARALLEL,
  // exactly as the reporter sends them, and Core refuses a count mismatch.
  for (const attachment of input.attachments) {
    form.append('attachment', attachment.file)
    form.append('attachmentScreen', attachment.screen)
  }
  const body = await request('POST', `${TRACKER_REPORTS_ROOT}/${input.number}/comments`, {
    body: form,
    idempotencyKey: input.idempotencyKey,
  })
  return reportDetail(body, input.number)
}

/**
 * Whether an attachment address is one this product serves itself. The
 * renderer loads an image only from here; a Forgejo asset address or any
 * foreign host is shown as text and never fetched.
 */
export function isTrackerReportAttachmentUrl(url: string): boolean {
  return /^\/api\/v1\/tracker\/reports\/\d+\/attachments\/\d+(?:\?[^#]*)?$/.test(url)
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1
}

function person(value: unknown): TrackerReportPerson {
  const record = asRecord(value)
  const username = asString(record?.['username'])
  const displayName = asString(record?.['displayName'])
  if (!record || username === null || displayName === null) {
    throw new CoreError('malformed', 'tracker report person is incomplete')
  }
  return { username, displayName }
}

function permissions(value: unknown): TrackerReportPermissions {
  const record = asRecord(value)
  if (!record) {
    throw new CoreError('malformed', 'tracker report permissions are missing')
  }
  const flags = ['edit', 'comment', 'close', 'delete'] as const
  for (const flag of flags) {
    if (typeof record[flag] !== 'boolean') {
      throw new CoreError('malformed', `tracker report permission ${flag} is not stated`)
    }
  }
  return {
    edit: record['edit'] === true,
    comment: record['comment'] === true,
    close: record['close'] === true,
    delete: record['delete'] === true,
  }
}

function attachment(value: unknown): TrackerReportAttachment {
  const record = asRecord(value)
  const name = asString(record?.['name'])
  const url = asString(record?.['url'])
  const size = record?.['size']
  if (!record || !isPositiveInteger(record['id']) || name === null || url === null || typeof size !== 'number') {
    throw new CoreError('malformed', 'tracker report attachment is incomplete')
  }
  if (!isTrackerReportAttachmentUrl(url)) {
    // An address that is not ours is not one this product will hand a reader.
    throw new CoreError('malformed', 'tracker report attachment address is not this product’s')
  }
  return { id: Number(record['id']), name, size, url }
}

function attachments(value: unknown): TrackerReportAttachment[] {
  // A Go nil slice ships as `null`; an absent list is an empty one.
  if (value === null || value === undefined) {
    return []
  }
  if (!Array.isArray(value)) {
    throw new CoreError('malformed', 'tracker report attachments are not a list')
  }
  return value.map((item) => attachment(item))
}

function comment(value: unknown): TrackerReportComment {
  const record = asRecord(value)
  const body = asString(record?.['body'])
  const createdAt = asString(record?.['createdAt'])
  const updatedAt = asString(record?.['updatedAt'])
  if (!record || !isPositiveInteger(record['id']) || body === null || createdAt === null || updatedAt === null) {
    throw new CoreError('malformed', 'tracker report comment is incomplete')
  }
  return {
    id: Number(record['id']),
    body,
    author: person(record['author']),
    createdAt,
    updatedAt,
    attachments: attachments(record['attachments']),
  }
}

function reportSummary(value: unknown): TrackerReportSummary {
  const record = asRecord(value)
  const title = asString(record?.['title'])
  const environment = asString(record?.['environment'])
  const state = record?.['state']
  const createdAt = asString(record?.['createdAt'])
  const updatedAt = asString(record?.['updatedAt'])
  const revision = asString(record?.['revision'])
  if (
    !record ||
    !isPositiveInteger(record['number']) ||
    title === null ||
    (environment !== 'development' && environment !== 'production') ||
    (state !== 'open' && state !== 'closed') ||
    typeof record['started'] !== 'boolean' ||
    createdAt === null ||
    updatedAt === null ||
    revision === null
  ) {
    throw new CoreError('malformed', 'tracker report summary is incomplete')
  }
  return {
    number: Number(record['number']),
    title,
    reporter: person(record['reporter']),
    environment,
    state,
    started: record['started'],
    createdAt,
    updatedAt,
    revision,
    permissions: permissions(record['permissions']),
  }
}

function reportDetail(value: unknown, expectedNumber: number): TrackerReportDetail {
  const summary = reportSummary(value)
  if (summary.number !== expectedNumber) {
    throw new CoreError('malformed', 'tracker report identity does not match the request')
  }
  const record = asRecord(value)
  const description = asString(record?.['description'])
  const comments = record?.['comments']
  if (!record || description === null || !(comments === null || comments === undefined || Array.isArray(comments))) {
    throw new CoreError('malformed', 'tracker report detail is incomplete')
  }
  return {
    ...summary,
    description,
    attachments: attachments(record['attachments']),
    comments: Array.isArray(comments) ? comments.map((item) => comment(item)) : [],
    attachmentWarning: asString(record['attachmentWarning']),
  }
}
